import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, ApiError } from '@/api/client';
import type {
  Account,
  CollaborationDistributionRow,
  ScoreboardRow,
  SubscriptionRollup,
  TenantTopFeature,
} from '@/api/types';
import { PERMISSIONS } from '@/api/types';
import { formatPercent } from '@/components/format';
import { Callout } from '@/components/Callout';
import { StatTile } from '@/components/StatTile';
import { formatCompact } from '@/components/TrendChart';
import { hasPermission } from '@/auth/session';
import {
  SeatsCell,
  StatusBadge,
  formatMoney,
  formatTrialCountdown,
} from '@/components/subscription';

const NEW_ACCOUNT_DAYS = 7;

/**
 * The chase lists, in the order somebody should work them.
 *
 * Deliberately not "every status": a filter strip that mirrors the schema makes the reader choose,
 * while one that mirrors the WORK tells them where to start. `missing` is here rather than hidden
 * because a tenant with no subscription row is one IAM's feature gate is failing OPEN for.
 */
const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'past_due', label: 'Past due' },
  { key: 'trial_ending', label: 'Trial ending' },
  { key: 'trial_lapsed', label: 'Trial lapsed' },
  { key: 'at_seat_limit', label: 'At seat limit' },
  { key: 'unused_apps', label: 'Paying, unused' },
  { key: 'missing', label: 'No subscription' },
] as const;

type FilterKey = (typeof FILTERS)[number]['key'];

/**
 * Tenant/account directory.
 *
 * Splits customers from internal environments on purpose. On this platform most tenants are
 * environments — prod/stg pairs of the same org, and seeded demos created seconds apart by a
 * script. Reporting them together as "accounts" overstates the customer base several times over,
 * so the KPI row states both numbers and the table hides internal tenants by default.
 */
export function AccountsPage() {
  // Money (prices, MRR) is subscription:read — the API withholds it from other roles, so the columns go too.
  const seesMoney = hasPermission(PERMISSIONS.subscriptionRead);
  // Defaults to TRUE: every tenant is listed unless you deliberately narrow the view.
  //
  // It used to default to false, which combined badly with a bug in how tenants were classified —
  // the collector only read an owner email from tenant_directory, and a tenant with no owner was
  // assumed to be an internal environment. Five of six real tenants were therefore hidden and the
  // page reported ONE customer account. The classification is fixed (the owner now comes from the
  // tenant's own Owner-profile holder), but hiding rows by default was always the wrong shape for
  // an estate view: you cannot notice what you cannot see.
  const [showInternal, setShowInternal] = useState(true);
  const [filter, setFilter] = useState<FilterKey>('all');

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['accounts', showInternal],
    queryFn: () => api.accounts(showInternal),
    retry: false,
  });

  // A separate read from the accounts list on purpose. A tenant that has never been scored is
  // ABSENT from this map rather than present with a zero, so the column renders "not scored" for it
  // instead of a number that would read as failing.
  const scoreboard = useQuery({
    queryKey: ['scoreboard'],
    queryFn: () => api.scores.scoreboard(),
    retry: false,
  });

  const scores = useMemo(
    () => new Map((scoreboard.data ?? []).map((s) => [s.tenant_id, s])),
    [scoreboard.data],
  );

  // Same posture as the scoreboard, twice over: separate reads joined by tenant_id, where an
  // absent row means "not collected" and renders as an em dash — never a zero, never a name.
  const collaboration = useQuery({
    queryKey: ['collaboration-distribution', showInternal],
    queryFn: () => api.collaboration.distribution(showInternal),
    retry: false,
  });

  const topFeatures = useQuery({
    queryKey: ['top-feature-per-tenant'],
    queryFn: () => api.telemetry.topFeaturePerTenant(),
    retry: false,
  });

  const shared = useMemo(
    () => new Map((collaboration.data?.tenants ?? []).map((t) => [t.tenant_id, t])),
    [collaboration.data],
  );

  const topFeature = useMemo(
    () => new Map((topFeatures.data ?? []).map((t) => [t.tenant_id, t])),
    [topFeatures.data],
  );

  const visible = useMemo(
    () => (data ? data.accounts.filter((a) => matches(a, filter)) : []),
    [data, filter],
  );

  if (isLoading) {
    return <div className="empty-state"><span className="spinner" /></div>;
  }

  if (isError) {
    return (
      <Callout tone="error" title="Accounts unavailable">
        {error instanceof ApiError ? error.message : 'Unexpected error.'}
      </Callout>
    );
  }

  if (!data) return null;

  // Rows sync one by one: the oldest row is what "as of" can honestly claim, and a spread says the last run was partial.
  const syncTimes = data.accounts.map((a) => a.synced_at).sort();
  const oldestSync = syncTimes[0];
  const newestSync = syncTimes.at(-1);
  const spread = oldestSync && newestSync ? Date.parse(newestSync) - Date.parse(oldestSync) : 0;
  // The side reads each feed one column; a failed one leaves "—" in every row, which must not read as "not scored".
  const failedColumns = [
    scoreboard.isError && 'Health',
    collaboration.isError && 'Shared',
    topFeatures.isError && 'Top feature',
  ].filter((x): x is string => typeof x === 'string');
  const rollup = data.subscriptions;

  return (
    <>
      <div className="kpi-row">
        <StatTile
          label="Customer accounts"
          value={data.customer_accounts}
          sub={data.internal_accounts > 0 ? `+${data.internal_accounts} internal` : undefined}
        />
        {/* MRR, not ARR, and internal environments excluded — see SubscriptionRollup. The subtitle
            carries the currency because a bare number is not money. */}
        {seesMoney && <StatTile
          label="MRR"
          value={formatMoney(rollup.mrr_minor, rollup.mrr_currency)}
          sub={
            rollup.mrr_is_mixed_currency
              ? `${rollup.mrr_currency} only — other currencies excluded`
              : (rollup.mrr_currency ?? 'nothing billable yet')
          }
          tone={rollup.mrr_is_mixed_currency ? 'warning' : 'default'}
        />}
        <StatTile label="Total users" value={data.total_users} />
        <StatTile label="Active (7d)" value={data.active_accounts_7d} sub="accounts with activity" />
        <StatTile label={`New (${NEW_ACCOUNT_DAYS}d)`} value={data.new_accounts} />
      </div>

      <SubscriptionAlerts rollup={rollup} onFilter={setFilter} />

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={`btn ${filter === f.key ? 'btn-primary' : 'btn-ghost'}`}
              style={{ padding: '3px 10px', fontSize: 12 }}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
              {f.key !== 'all' && (
                <span style={{ opacity: 0.7, marginLeft: 5 }}>
                  {data.accounts.filter((a) => matches(a, f.key)).length}
                </span>
              )}
            </button>
          ))}
        </div>

        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }} className="t-label">
          <input
            type="checkbox"
            checked={showInternal}
            onChange={(e) => setShowInternal(e.target.checked)}
          />
          Include internal environments
        </label>
        {oldestSync && (
          <span className="t-label subtle" title={spread > 3_600_000 ? `newest row ${formatWhen(newestSync!)}` : undefined}>
            data as of {formatWhen(oldestSync)}
            {spread > 3_600_000 ? ` (some rows newer, up to ${formatWhen(newestSync!)})` : ''} — refreshed by the collector, not live
          </span>
        )}
      </div>

      {failedColumns.length > 0 && (
        <Callout tone="warning" title={`${failedColumns.join(', ')} could not be loaded`}>
          Those columns show — on every row because the read failed, not because the accounts have nothing there. Reload to try again.
        </Callout>
      )}

      {data.has_count_errors && (
        <Callout tone="warning" title="Some user counts could not be read">
          Each account's users live in its own database, so one unreachable database affects only
          its row. Those show <strong>?</strong> rather than 0 — the account is not empty, it just
          could not be counted.
        </Callout>
      )}

      <AccountsTable
        accounts={visible}
        filter={filter}
        scores={scores}
        shared={shared}
        topFeature={topFeature}
      />
    </>
  );
}

/**
 * The commercially urgent things, above the table.
 *
 * Each one is clickable and sets the matching filter, because a count you cannot act on is trivia.
 * Rendered as separate callouts rather than one combined line so that a platform with three
 * simultaneous problems does not compress them into a sentence nobody reads.
 */
function SubscriptionAlerts({
  rollup,
  onFilter,
}: {
  rollup: SubscriptionRollup;
  onFilter: (key: FilterKey) => void;
}) {
  return (
    <>
      {/* First, because it is the only one that is a hole in ENFORCEMENT rather than in revenue:
          IAM's feature gate fails open for a tenant with no subscription row, so these tenants have
          every app regardless of what they pay. */}
      {rollup.missing_subscriptions > 0 && (
        <Callout tone="warning" title={`${rollup.missing_subscriptions} account(s) have no subscription`}>
          IAM's feature gate <strong>fails open</strong> for these — they get every app regardless of
          plan. Run <code>POST /api/v1/iam/internal/subscriptions/backfill</code> to close it.{' '}
          <FilterLink onClick={() => onFilter('missing')} />
        </Callout>
      )}

      {rollup.past_due > 0 && (
        <Callout tone="warning" title={`${rollup.past_due} account(s) past due`}>
          Access is unchanged — past due does not cut anyone off. These need chasing, not suspending.{' '}
          <FilterLink onClick={() => onFilter('past_due')} />
        </Callout>
      )}

      {rollup.trials_lapsed > 0 && (
        <Callout tone="warning" title={`${rollup.trials_lapsed} trial(s) expired but still marked trialing`}>
          IAM's lifecycle sweep has not moved these to past due — it may be behind, or not running.{' '}
          <FilterLink onClick={() => onFilter('trial_lapsed')} />
        </Callout>
      )}

      {rollup.trials_ending_within_7_days > 0 && (
        <Callout tone="info" title={`${rollup.trials_ending_within_7_days} trial(s) ending within 7 days`}>
          The conversion window. <FilterLink onClick={() => onFilter('trial_ending')} />
        </Callout>
      )}

      {rollup.at_seat_limit > 0 && (
        <Callout tone="info" title={`${rollup.at_seat_limit} account(s) at their seat limit`}>
          Their next invite will be refused — an upgrade conversation, and a support ticket if nobody
          starts it. <FilterLink onClick={() => onFilter('at_seat_limit')} />
        </Callout>
      )}

      {rollup.paying_for_unused_apps > 0 && (
        <Callout tone="info" title={`${rollup.paying_for_unused_apps} account(s) pay for apps they never open`}>
          Either a failed rollout or a downgrade waiting to happen.{' '}
          <FilterLink onClick={() => onFilter('unused_apps')} />
        </Callout>
      )}

      {rollup.purge_eligible > 0 && (
        <Callout tone="warning" title={`${rollup.purge_eligible} churned account(s) past their retention window`}>
          Their data is purge-eligible. Nothing is deleted automatically — a human decides.
        </Callout>
      )}
    </>
  );
}

function FilterLink({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="t-label"
      style={{
        background: 'none',
        border: 'none',
        padding: 0,
        color: 'var(--primary)',
        cursor: 'pointer',
        textDecoration: 'underline',
      }}
    >
      Show them
    </button>
  );
}

/** True when the account belongs in the given chase list. */
function matches(account: Account, filter: FilterKey): boolean {
  const s = account.subscription;

  switch (filter) {
    case 'all':
      return true;
    case 'past_due':
      return s.status === 'past_due';
    case 'trial_ending':
      return (
        s.status === 'trialing' &&
        account.trial_days_remaining !== null &&
        account.trial_days_remaining >= 0 &&
        account.trial_days_remaining <= 7
      );
    case 'trial_lapsed':
      return s.status === 'trialing' && (account.trial_days_remaining ?? 0) < 0;
    case 'at_seat_limit':
      return account.is_at_seat_limit;
    case 'unused_apps':
      // Only where money is actually involved: an internal environment with an unopened app is not
      // a commercial problem, and listing it would bury the ones that are.
      return (
        s.is_billable !== false &&
        (account.mrr_minor ?? 0) > 0 &&
        account.entitled_unused_apps.length > 0
      );
    case 'missing':
      return !s.is_replicated;
    default:
      return true;
  }
}

/**
 * The health number, or an honest absence.
 *
 * Three distinct states, and collapsing any two of them is the mistake this component exists to
 * prevent: never scored (no row), scored but nothing measurable (`unscored`), and an actual number.
 * Only the third is a judgement about the customer.
 */
function HealthCell({ score }: { score: ScoreboardRow | undefined }) {
  if (!score) {
    return <span className="muted" title="This account has not been scored yet.">—</span>;
  }

  if (score.unscored) {
    return (
      <span className="muted" title="Nothing this policy measures could be read for this account.">
        n/a
      </span>
    );
  }

  return (
    <span className="mono" title={`${score.score_version} · ${formatPercent(score.coverage)} measured`}>
      {Math.round(score.total)}
    </span>
  );
}

/** The COUNT of raised flags. Never a probability — there is no calibration for one. */
function RiskBadge({ level }: { level: number }) {
  if (level === 0) return null;

  return (
    <span className={`badge-risk${level < 2 ? ' badge-risk-warning' : ''}`}>
      {level} {level === 1 ? 'FLAG' : 'FLAGS'}
    </span>
  );
}

function AccountsTable({
  accounts, filter, scores, shared, topFeature,
}: {
  accounts: Account[];
  filter: FilterKey;
  scores: Map<string, ScoreboardRow>;
  shared: Map<string, CollaborationDistributionRow>;
  topFeature: Map<string, TenantTopFeature>;
}) {
  const seesMoney = hasPermission(PERMISSIONS.subscriptionRead);

  // Off by default, so the table keeps its "most recently active first" order until somebody asks
  // a different question. Ascending first, because the interesting end of a health column is the
  // bottom — nobody opens this page to admire the healthiest account.
  const [byScore, setByScore] = useState(false);

  const rows = useMemo(() => {
    if (!byScore) return accounts;

    return [...accounts].sort((a, b) => {
      const left = scores.get(a.tenant_id);
      const right = scores.get(b.tenant_id);

      // Unscored sorts LAST in either direction. It is not a low score, and letting it head an
      // ascending sort would put "we could not measure this" where "this is in trouble" belongs.
      const l = left && !left.unscored ? left.total : Number.POSITIVE_INFINITY;
      const r = right && !right.unscored ? right.total : Number.POSITIVE_INFINITY;

      return l - r;
    });
  }, [accounts, byScore, scores]);

  if (accounts.length === 0) {
    return (
      <div className="empty-state t-body">
        {filter === 'all'
          ? 'No accounts. Run the collector to populate.'
          : 'Nothing in this list — which is the good outcome.'}
      </div>
    );
  }

  const cutoff = Date.now() - NEW_ACCOUNT_DAYS * 86_400_000;

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="t-heading" style={{ margin: 0 }}>Accounts</h2>
        <span className="t-label subtle">
          {byScore ? 'lowest health first' : 'most recently active first'}
        </span>
      </div>
      <table className="table">
        <thead>
          <tr>
            <th>Account</th>
            <th className="num">
              <button type="button" className="th-sort" onClick={() => setByScore((v) => !v)}>
                Health
              </button>
            </th>
            <th>Plan</th>
            <th>Status</th>
            {seesMoney && <th className="num">MRR</th>}
            <th className="num">Seats</th>
            <th className="num">Events (30d)</th>
            <th className="num">Shared</th>
            <th>Top feature</th>
            <th>Apps used</th>
            <th>Last active</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((a) => (
            <tr key={a.tenant_id}>
              <td>
                <Link to={`/accounts/${a.tenant_id}`} style={{ color: 'var(--primary)', fontWeight: 600 }}>
                  {a.name || '(unnamed)'}
                </Link>
                {Date.parse(a.created_at) >= cutoff && <span className="badge-new">NEW</span>}
                {a.is_internal && <span className="badge-muted">internal</span>}
                <RiskBadge level={scores.get(a.tenant_id)?.risk_level ?? 0} />
                <div className="t-caption muted">
                  {a.owner_name ? `${a.owner_name} · ` : ''}
                  {a.owner_email || 'no owner recorded'}
                </div>
              </td>
              <td className="num"><HealthCell score={scores.get(a.tenant_id)} /></td>
              <td>
                <span className="mono t-label">{a.subscription.plan_code ?? '—'}</span>
                {/* An internal plan is never billed; saying so inline stops anyone reading the
                    dash in the MRR column as missing data. */}
                {a.subscription.is_billable === false && (
                  <span className="badge-muted">not billed</span>
                )}
              </td>
              <td>
                <StatusBadge subscription={a.subscription} />
                {a.subscription.status === 'trialing' && (
                  <div className="t-caption muted">
                    ends {formatTrialCountdown(a.trial_days_remaining) ?? '—'}
                  </div>
                )}
              </td>
              {seesMoney && <td className="num">{formatMoney(a.mrr_minor, a.subscription.currency)}</td>}
              <td className="num">
                <SeatsCell
                  seatsUsed={a.seats_used}
                  seatLimit={a.subscription.seat_limit}
                  isAtLimit={a.is_at_seat_limit}
                />
                {/* Seats count pending invitations, so the seat figure alone cannot answer "how many
                    of these people actually exist and are active". Kept as a sub-line rather than a
                    column: it is context for the number above it, not a peer of it. */}
                <div className="t-caption muted">
                  {a.active_user_count === null ? '? active' : `${a.active_user_count} active`}
                  {(a.pending_invitation_count ?? 0) > 0 && `, ${a.pending_invitation_count} invited`}
                </div>
              </td>
              <td className="num">{formatCompact(a.event_count_30d)}</td>
              <td className="num"><SharedCell row={shared.get(a.tenant_id)} /></td>
              <td><TopFeatureCell row={topFeature.get(a.tenant_id)} /></td>
              <td><AppsCell account={a} /></td>
              <td className="muted">{a.last_activity_at ? formatWhen(a.last_activity_at) : 'never'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The share of this account's objects that are shared — "is this a team, or five people who
 * happen to share a login page?"
 *
 * Null and absent both render as an em dash: "sharing was not collected" is a statement about
 * measurement, and showing it as 0% would claim the account collaborates not at all.
 */
function SharedCell({ row }: { row: CollaborationDistributionRow | undefined }) {
  if (!row) {
    return <span className="muted" title="Sharing was not collected for this account.">—</span>;
  }

  if (row.collaboration_ratio === null) {
    // Collected, but nothing to divide — a different statement from "not collected", kept
    // distinguishable in the title even though both render as a dash.
    return <span className="muted" title="Share tables were read, but there is nothing to divide a ratio from.">—</span>;
  }

  return (
    <span
      className="mono"
      title={row.active_shares !== null ? `${row.active_shares} active share(s)` : undefined}
    >
      {formatPercent(row.collaboration_ratio)}
    </span>
  );
}

/**
 * The account's single most-used feature over the collector's 30-day window, by registry name
 * ("Blueprints", "Reports"). An account with nothing in the window shows an em dash — absence is
 * "not collected", never a fabricated favourite.
 */
function TopFeatureCell({ row }: { row: TenantTopFeature | undefined }) {
  if (!row) {
    return <span className="muted" title="No feature usage collected in the window.">—</span>;
  }

  return (
    <span title={`${row.app_key}:${row.target_type} · ${formatCompact(row.event_count)} events (30d)`}>
      {row.display_name}
      {/* The phase-1 rule: an unannotated feature is flagged, never hidden behind a tidy label. */}
      {!row.is_known && <span className="badge-muted" title="Not yet annotated in the feature registry">unnamed</span>}
    </span>
  );
}

/**
 * Apps used, with unused-but-enabled shown greyed.
 *
 * The gap is the point: an app switched on and never opened is a failed rollout, and it is
 * invisible if you only render one of the two lists.
 */
function AppsCell({ account }: { account: Account }) {
  const used = new Set(account.apps_used);
  const unused = account.apps_enabled.filter((a) => !used.has(a));

  if (account.apps_used.length === 0 && unused.length === 0) {
    return <span className="subtle">—</span>;
  }

  return (
    <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
      {account.apps_used.map((a) => (
        <span key={a} className="chip chip-on">{a}</span>
      ))}
      {unused.map((a) => (
        <span key={a} className="chip chip-off" title="Enabled but never opened">{a}</span>
      ))}
    </span>
  );
}

export function formatWhen(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;

  // Minutes first: every "synced …" label uses this, and "just now" for a 50-minute-old sync hid staleness.
  const minutes = (Date.now() - date.getTime()) / 60_000;
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${Math.floor(minutes)} min ago`;

  const hours = minutes / 60;
  if (hours < 24) return `${Math.floor(hours)}h ago`;
  if (hours < 24 * 7) return `${Math.floor(hours / 24)}d ago`;

  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}
